/**
 * `<RequirePermission>` (Порція 3.0.5.5).
 *
 * Обгортка для умовного рендеру UI-елементів на основі permissions.
 *
 * Використання:
 *   <RequirePermission code="admin.users.manage">
 *     <button>Створити користувача</button>
 *   </RequirePermission>
 *
 *   // Або декілька кодів — досить одного:
 *   <RequirePermission anyOf={['sales.realization.create', 'sales.realization.post']}>
 *     <button>...</button>
 *   </RequirePermission>
 *
 *   // З fallback:
 *   <RequirePermission code="payroll.manage" fallback={<DisabledHint />}>
 *     <button>...</button>
 *   </RequirePermission>
 *
 * Семантика контракту (3.0.5.5):
 *  - OWNER/ADMIN tenant'а — завжди true, бачать усе.
 *  - MEMBER — бачить лише якщо у me.permissions[] є потрібний код.
 *
 * Поки backend НЕ enforce'ить permissions (це 3.0.7), цей компонент —
 * UX-захист: ховає елементи, які user і так не зможе використати.
 */
import type { ReactNode } from 'react';
import { usePermissions } from './usePermissions';

interface BaseProps {
  children: ReactNode;
  /** Альтернативний рендер коли permission відсутній. За замовч. — null. */
  fallback?: ReactNode;
}

type Props =
  | (BaseProps & { code: string; anyOf?: never })
  | (BaseProps & { anyOf: readonly string[]; code?: never });

export default function RequirePermission(props: Props) {
  const { has, hasAny } = usePermissions();
  const allowed = 'code' in props && props.code
    ? has(props.code)
    : 'anyOf' in props && props.anyOf
    ? hasAny(props.anyOf)
    : false;
  return <>{allowed ? props.children : props.fallback ?? null}</>;
}
